import { Check, ChevronDown, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SelectOption } from "./SignUpTypes";

type SignUpSelectProps = {
    value: string;
    placeholder: string;
    options: SelectOption[];
    disabled?: boolean;
    showDialCode?: boolean;
    searchable?: boolean;
    onChange: (value: string) => void;
};

const SignUpSelect = ({
    value,
    placeholder,
    options,
    disabled = false,
    showDialCode = false,
    searchable,
    onChange,
}: SignUpSelectProps) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const selectedOption = options.find((option) => option.value.toLowerCase() === value.toLowerCase()) ||
        options.find((option) => option.value === value);

    const isSearchEnabled = searchable !== undefined ? searchable : options.length > 6;

    const filteredOptions = searchTerm.trim()
        ? options.filter(
              (option) =>
                  option.label.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
                  (option.dialCode && option.dialCode.includes(searchTerm.trim()))
          )
        : options;

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm("");
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        if (isOpen && isSearchEnabled && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 50);
        }
        if (!isOpen) {
            setSearchTerm("");
        }
    }, [isOpen, isSearchEnabled]);

    return (
        <div
            ref={rootRef}
            className={`signup-select ${isOpen ? "is-open" : ""} ${
                disabled ? "is-disabled" : ""
            }`}
        >
            <button
                type="button"
                className="signup-select-trigger"
                disabled={disabled}
                onClick={() => setIsOpen((current) => !current)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span
                    className={`signup-select-value ${
                        selectedOption ? "has-value" : ""
                    }`}
                >
                    {selectedOption?.flag && (
                        <span className="signup-country-flag">
                            {selectedOption.flag}
                        </span>
                    )}
                    <span>{selectedOption?.label || placeholder}</span>
                </span>
                <ChevronDown size={16} strokeWidth={1.8} />
            </button>

            {isOpen && (
                <div className="signup-select-menu" role="listbox">
                    {isSearchEnabled && (
                        <div className="signup-select-search-wrap" onClick={(e) => e.stopPropagation()}>
                            <Search size={14} className="signup-select-search-icon" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="signup-select-search"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}

                    <div className="signup-select-options-scroll">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => {
                                const isSelected =
                                    option.value === value ||
                                    option.value.toLowerCase() === value.toLowerCase();

                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`signup-select-option ${
                                            isSelected ? "is-selected" : ""
                                        }`}
                                        onClick={() => {
                                            onChange(option.value);
                                            setIsOpen(false);
                                            setSearchTerm("");
                                        }}
                                        role="option"
                                        aria-selected={isSelected}
                                    >
                                        <span className="signup-select-option-main">
                                            {option.flag && (
                                                <span className="signup-country-flag">
                                                    {option.flag}
                                                </span>
                                            )}
                                            <span>{option.label}</span>
                                        </span>

                                        <span className="signup-select-option-end">
                                            {showDialCode && option.dialCode && (
                                                <small>{option.dialCode}</small>
                                            )}
                                            {isSelected && (
                                                <Check
                                                    size={15}
                                                    strokeWidth={2.2}
                                                />
                                            )}
                                        </span>
                                    </button>
                                );
                            })
                        ) : (
                            <div className="signup-select-empty">
                                No options found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignUpSelect;
